

import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { sh6RegisterApi } from "../../../api/Sh6RegisterApi";
import { Button } from "../../../components/Button";
import { Spinner } from "reactstrap";
import { X } from "lucide-react";

// ── SH-6 columns ─────────────────────────────────────────────────────────────
const DETAIL_COLUMNS = [
  { key: "serialNo", label: "S.No", col: "(1)" },
  { key: "nameOfGrantee", label: "Name of Grantee", col: "(2)" },
  { key: "numberOfOptionsGranted", label: "Number of Options Granted", col: "(3)" },
  { key: "dateOptionsVested", label: "Date Options Vested", col: "(4)" },
  { key: "exercisedPeriod", label: "Exercised Period", col: "(5)" },
  { key: "dateOptionsExercised", label: "Date Options Exercised", col: "(6)" },
  { key: "optionsExercised", label: "Options Exercised", col: "(7)" },
  { key: "exercisePrice", label: "Exercise Price (₹)", col: "(8)" },
  { key: "numberOfSharesArising", label: "Number of Shares Arising", col: "(9)" },
  { key: "folioNo", label: "Folio No.", col: "(10)" },
  { key: "lockInPeriod", label: "Lock-in Period", col: "(11)" },
  { key: "optionLapsed", label: "Options Lapsed", col: "(12)" },
  { key: "totalOptionsInForce", label: "Total Options in Force", col: "(13)" },
  { key: "amountForfeitedOrRefunded", label: "Amount Forfeited / Refunded (₹)", col: "(14)" },
  { key: "variationOfTerms", label: "Variation of Terms", col: "(15)" },
  { key: "signature", label: "Signature", col: "(16)" },
  { key: "remarks", label: "Remarks", col: "(17)" },
];

// Split: Part 1 = cols 1–9, Part 2 = S.No + cols 10–17
const PART_ONE = DETAIL_COLUMNS.slice(0, 9);   // (1)–(9)
const PART_TWO = [DETAIL_COLUMNS[0], ...DETAIL_COLUMNS.slice(9)]; // S.No + (10)–(17)

const BLANK_ENTRY = {
  nameOfGrantee: "", numberOfOptionsGranted: "", dateOptionsVested: "",
  exercisedPeriod: "", dateOptionsExercised: "", optionsExercised: "",
  exercisePrice: "", numberOfSharesArising: "", folioNo: "",
  lockInPeriod: "", optionLapsed: "", totalOptionsInForce: "",
  amountForfeitedOrRefunded: "", variationOfTerms: "", signature: "", remarks: "",
};

const dateFields = ["dateOptionsVested", "dateOptionsExercised"];
const numberFields = [
  "numberOfOptionsGranted", "optionsExercised", "exercisePrice",
  "numberOfSharesArising", "lockInPeriod", "optionLapsed",
  "totalOptionsInForce", "amountForfeitedOrRefunded",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => (v === null || v === undefined || v === "") ? "—" : String(v);

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return "—"; }
};

const formatAddress = (addr) => {
  if (!addr) return "—";
  return [addr.address, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
};

// ── Sub-table for screen ──────────────────────────────────────────────────────
const SubTable = ({ columns, rows, partLabel }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5">
      <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">{partLabel}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            {columns.map(col => (
              <th key={col.key} className="border border-slate-600 px-2 py-1 text-center font-bold text-[9px] whitespace-nowrap">
                {col.col}
              </th>
            ))}
          </tr>
          <tr className="bg-slate-700 text-white">
            {columns.map(col => (
              <th
                key={col.key}
                className="border border-slate-600 px-2 py-2 text-center font-semibold text-[9px] leading-tight"
                style={{ minWidth: col.key === "serialNo" ? 36 : 100 }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {columns.map(col => (
                <td key={col.key} className="border border-slate-200 px-2 py-1.5 text-center text-[10px] align-middle">
                  {dateFields.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Add Entry Modal ───────────────────────────────────────────────────────────
const AddEntryModal = ({ onClose, onSave, saving, parentRecords }) => {
  const [selectedParentId, setSelectedParentId] = useState(
    parentRecords.length === 1 ? parentRecords[0].id : ""
  );
  const [form, setForm] = useState({ ...BLANK_ENTRY });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-3 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-800">
            Add New SH-6 Entry
          </h1>
          <button
            onClick={onClose}
            aria-label="Close"
            type="button"
            className="p-1 rounded-md bg-red-50 hover:bg-red-100 text-red-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {parentRecords.length > 1 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Select Resolution Record *</label>
              <select
                value={selectedParentId}
                onChange={e => setSelectedParentId(e.target.value)}
                className="w-full px-2 py-1.5 border border-blue-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— Select —</option>
                {parentRecords.map(p => (
                  <option key={p.id} value={p.id}>
                    Resolution: {fmtDate(p.dateOfSpecialResolution)} | {p.companyName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {DETAIL_COLUMNS.filter(c => c.key !== "serialNo").map(col => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{col.col} {col.label}</label>
                <input
                  type={dateFields.includes(col.key) ? "date" : numberFields.includes(col.key) ? "number" : "text"}
                  value={form[col.key] || ""}
                  onChange={e => setForm(prev => ({ ...prev, [col.key]: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            ))}
          </div>
        </div>
        {/* <div className="px-6 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 font-semibold uppercase">
            Cancel
          </button>
          <button
            onClick={() => onSave(form, selectedParentId)}
            disabled={saving || !selectedParentId}
            className="px-4 py-2 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 font-semibold uppercase"
          >
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div> */}
        <div className="flex flex-row items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-red-50 py-2 px-4 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
          >
            Cancel
          </button>
          {saving ?
            <Button label={<Spinner size={"sm"} />} /> :
            <Button label={`${saving ? "Update" : "Save Entry"}`} disabled={saving || !selectedParentId} onClick={() => onSave(form, selectedParentId)} />}
        </div>
      </div>
    </div>
  );
};

// ── PDF print styles ──────────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    @page { size: A3 landscape; margin: 8mm; }
    body * { visibility: hidden !important; }
    #sh6-pdf-area, #sh6-pdf-area * { visibility: visible !important; }
    #sh6-pdf-area { position: fixed; top: 0; left: 0; width: 100%; font-family: Arial, sans-serif; }
  }
`;

// ── Main SH-6 Viewer ──────────────────────────────────────────────────────────
const Sh6Viewer = ({ selectedClientData }) => {
  const token = useSelector((state) => state.auth.access_token);
  const accountId = selectedClientData?.accountId || selectedClientData?.id;

  const [parentRecords, setParentRecords] = useState([]);
  const [flatRows, setFlatRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Inject print styles once
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "sh6-print-style";
    style.innerHTML = PRINT_STYLES;
    if (!document.getElementById("sh6-print-style")) document.head.appendChild(style);
    return () => document.getElementById("sh6-print-style")?.remove();
  }, []);

  const flattenRecords = (records) => {
    const rows = [];
    records.forEach(parent => {
      (parent.sh6EmployeeStockDetails || []).forEach(detail => {
        rows.push({ ...detail, _parentId: parent.id });
      });
    });
    return rows;
  };

  const fetchData = useCallback(async () => {
    if (!accountId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await sh6RegisterApi.fetchSh6Register(accountId, token);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setParentRecords(list);
      setFlatRows(flattenRecords(list));
    } catch (err) {
      setError("No data available for SH-6");
    } finally {
      setLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveEntry = async (form, parentId) => {
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        ...form,
        numberOfOptionsGranted: form.numberOfOptionsGranted ? parseInt(form.numberOfOptionsGranted, 10) : null,
        optionsExercised: form.optionsExercised ? parseInt(form.optionsExercised, 10) : null,
        exercisePrice: form.exercisePrice ? parseFloat(form.exercisePrice) : null,
        numberOfSharesArising: form.numberOfSharesArising ? parseInt(form.numberOfSharesArising, 10) : null,
        lockInPeriod: form.lockInPeriod ? parseInt(form.lockInPeriod, 10) : null,
        optionLapsed: form.optionLapsed ? parseInt(form.optionLapsed, 10) : null,
        totalOptionsInForce: form.totalOptionsInForce ? parseInt(form.totalOptionsInForce, 10) : null,
        amountForfeitedOrRefunded: form.amountForfeitedOrRefunded ? parseFloat(form.amountForfeitedOrRefunded) : null,
      };
      await sh6RegisterApi.addNewEmployeStock( parentId,payload, token);
      setShowModal(false);
      setStatus({ type: "success", message: "Entry added successfully!" });
      await fetchData();
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Failed to add entry." });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = () => {
    setGeneratingPdf(true);
    setTimeout(() => { window.print(); setGeneratingPdf(false); }, 300);
  };

  if (!selectedClientData) {
    return <p className="text-center text-gray-500 mt-10">Select an account to view SH-6 data.</p>;
  }

  // ── PDF sub-table builder ─────────────────────────────────────────────────
  const PdfSubTable = ({ columns, rows, partLabel }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 8, fontWeight: "bold", color: "#000", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.8 }}>
        {partLabel}
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#1e3a5f", color: "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "2px 3px", textAlign: "center", fontSize: 12, whiteSpace: "nowrap" }}>
                {col.col}
              </th>
            ))}
          </tr>
          <tr style={{ background: "#2d4f7c", color: "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "3px 3px", textAlign: "center", fontSize: 12, lineHeight: 1.3, minWidth: col.key === "serialNo" ? 24 : 72 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#000" : "#000" }}>
              {columns.map(col => (
                <td key={col.key} style={{ border: "1px solid #000", padding: "2px 3px", textAlign: "center", fontSize: 10, verticalAlign: "middle" }}>
                  {dateFields.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="bg-white border-b-4 px-6 py-2 mb-2 text-center shadow-sm rounded-t-lg">
        <h1 className="text-2xl font-black uppercase tracking-tight">Form No. SH - 6</h1>
        <h2 className="text-lg font-bold mt-1">Register of Employee Stock Options</h2>
        <p className="text-[10px] italic text-slate-500 mt-1 max-w-4xl mx-auto">
          [Pursuant to section 62(1)(b) of the Companies Act, 2013 and rule 12(10) of the
          Companies (Share Capital and Debentures) Rules 2014]
        </p>
      </div>

      {/* ── Controls ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => { setShowModal(true); setStatus(null); }}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 transition"
          >
            + Add New Entry
          </button>
          <button
            onClick={fetchData}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 transition"
          >
            🔄 Reload
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={flatRows.length === 0 || generatingPdf}
            className="bg-green-600 text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* ── Status Banner ── */}
      {status && (
        <div className={`mb-2 px-4 py-3 rounded-lg text-sm font-medium border ${status.type === "success"
            ? "bg-green-50 text-green-800 border-green-200"
            : "bg-red-50 text-red-800 border-red-200"
          }`}>
          {status.message}
        </div>
      )}

      {/* ── Screen: per-resolution groups, each split into two parts ── */}
      {loading ? (
        <div className="text-center py-10 text-gray-500 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      ) : flatRows.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm italic">
          No entries found for this company.
        </div>
      ) : (
        <>
          {parentRecords.map((parent) => {
            const rows = parent.sh6EmployeeStockDetails || [];
            const addr = formatAddress(parent.registeredOfficeAddress);
            return (
              <div key={parent.id} className="mb-6">

                {/* ── Company info bar — shown ONCE per resolution ── */}
                <div className="bg-slate-800 text-white px-4 py-2 rounded-t-xl flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span><span className="text-slate-400 mr-1">Company:</span><b>{parent.companyName}</b></span>
                  <span><span className="text-slate-400 mr-1">Special Resolution Date:</span><b>{fmtDate(parent.dateOfSpecialResolution)}</b></span>
                  <span><span className="text-slate-400 mr-1">Address:</span><b>{addr}</b></span>
                </div>

                {/* ── Part 1: Columns (1)–(9) ── */}
                <div className="mt-0 rounded-t-none">
                  <SubTable columns={PART_ONE} rows={rows} partLabel="Part 1 — Columns (1) to (9)" />
                </div>

                {/* ── Part 2: S.No + Columns (10)–(17) ── */}
                <div className="mt-3">
                  <SubTable columns={PART_TWO} rows={rows} partLabel="Part 2 — Columns (10) to (17)" />
                </div>

                <div className="text-right text-xs text-gray-500 mt-1 pr-1">
                  Entries in this resolution: <span className="font-bold text-gray-700">{rows.length}</span>
                </div>
              </div>
            );
          })}

          <div className="text-right text-xs text-gray-500 mb-4 pr-1">
            Total entries: <span className="font-bold text-gray-700">{flatRows.length}</span>
          </div>
        </>
      )}

      {/* ── Hidden PDF Print Area ── */}
      {flatRows.length > 0 && (
        <div id="sh6-pdf-area" style={{ display: "none" }}>
          <style>{`@media print { #sh6-pdf-area { display: block !important; } }`}</style>

          {/* Global PDF title */}
          <div style={{ textAlign: "center", marginBottom: 10, fontFamily: "Arial, sans-serif" }}>
            <h1 style={{ fontSize: 14, fontWeight: "bold", margin: 0 }}>Form No. SH - 6</h1>
            <h2 style={{ fontSize: 12, margin: "2px 0" }}>Register of Employee Stock Options</h2>
            <p style={{ fontSize: 10, margin: 0, color: "#000q", fontStyle: "italic" }}>
              [Pursuant to section 62(1)(b) of the Companies Act, 2013 and rule 12(10) of the
              Companies (Share Capital and Debentures) Rules 2014]
            </p>
          </div>

          {/* Per-resolution PDF blocks */}
          {parentRecords.map((parent, idx) => {
            const rows = parent.sh6EmployeeStockDetails || [];
            const addr = formatAddress(parent.registeredOfficeAddress);
            return (
              <div key={parent.id} style={{ marginBottom: 18, fontFamily: "Arial, sans-serif" }}>

                {/* Company info — shown ONCE per resolution in PDF */}
                <div style={{
                  background: "#1e3a5f", color: "#000",
                  padding: "5px 8px", marginBottom: 6,
                  fontSize: 7.5, borderRadius: 3,
                  display: "flex", flexWrap: "wrap", gap: "12px"
                }}>
                  <span><span style={{ color: "#000", fontSize: 10 }}>Company: </span><b>{parent.companyName}</b></span>
                  <span><span style={{ color: "#000", fontSize: 10 }}>Special Resolution Date: </span><b>{fmtDate(parent.dateOfSpecialResolution)}</b></span>
                  <span><span style={{ color: "#000", fontSize: 10 }}>Address: </span><b>{addr}</b></span>
                </div>

                {/* PDF Part 1 */}
                <PdfSubTable columns={PART_ONE} rows={rows} partLabel="Part 1 — Columns (1) to (9)" />

                {/* PDF Part 2 */}
                <PdfSubTable columns={PART_TWO} rows={rows} partLabel="Part 2 — Columns (10) to (17)" />

                {/* Divider between resolution groups */}
                {idx < parentRecords.length - 1 && (
                  <div style={{ borderTop: "1.5px dashed #94a3b8", marginTop: 14 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add Entry Modal ── */}
      {showModal && (
        <AddEntryModal
          onClose={() => setShowModal(false)}
          onSave={handleSaveEntry}
          saving={saving}
          parentRecords={parentRecords}
        />
      )}
    </div>
  );
};

export default Sh6Viewer;